<template>
  <div v-if="!isSyncTargetsLoading" ref="invoiceForm">
    <batch-form-fields
      v-if="batch !== undefined && batchSyncTargets !== undefined"
      :batch="batch"
      :batch-sync-targets="batchSyncTargets"
      :saving="saving"
      @save-clicked="handleSaveClicked"
    />
  </div>
  <div v-else class="d-flex align-center justify-center" style="height: 100vh">
    <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
  </div>
</template>

<script setup lang="ts">
import { BatchFormData, BatchFormSyncTarget, BatchPaymentTerms } from '@/models/batch'
import moment from 'moment/moment'
import { computed, ref } from 'vue'
import {
  CustomDataWithSchema,
  useInvoiceQuery,
  useInvoiceSyncTargetsQuery,
  useUpdateInvoiceMutation,
} from '@/api/invoice'
import { AxiosError } from 'axios'
import BatchFormFields from '@/components/batches/BatchFormFields.vue'

const props = defineProps<{
  id: string
}>()
const idRef = computed(() => props.id)
const { data: invoiceData } = useInvoiceQuery(idRef)

const { data: syncTargetsData, isLoading: isSyncTargetsLoading } = useInvoiceSyncTargetsQuery(idRef)

const batch = computed<BatchFormData | undefined>(() => {
  if (!invoiceData.value) return undefined
  return {
    id: invoiceData.value.id,
    invoiceNumber: invoiceData.value.invoiceNumber,
    invoiceDate: invoiceData.value.invoiceDate,
    paymentTerms: invoiceData.value.paymentTerms,
    dueDate: invoiceData.value.dueDate,
    memo: invoiceData.value.memo,
  }
})

const batchSyncTargets = computed<BatchFormSyncTarget[] | undefined>(() => {
  if (!syncTargetsData.value) return undefined
  return syncTargetsData.value.map((t) => ({
    id: t.id,
    name: t.name,
    customFieldsSchemaId: t.customFieldsSchemaId,
    customData: t.customData,
  }))
})

const { mutateAsync: updateInvoice, isLoading: saving, error } = useUpdateInvoiceMutation()

const errorMessage = computed(() => {
  if (error.value && error.value?.response?.status === 409) {
    return `Batch number already exists`
  } else {
    return error.value
  }
})

const invoiceForm = ref()
const emit = defineEmits<{
  (e: 'batch-updated')
  (e: 'show-snackbar-error', value: string | AxiosError<unknown, any> | null)
  (e: 'show-snackbar', value: string)
}>()

async function handleSaveClicked(formData: {
  id: string
  customDataPerApp: Record<string, CustomDataWithSchema>
  invoiceNumber: string | null | undefined
  invoiceDate: moment.Moment | undefined
  paymentTerms: BatchPaymentTerms | null
  dueDate: moment.Moment | undefined
  jobIds: undefined
  memo: string | null | undefined
}) {
  try {
    await updateInvoice({
      id: formData.id,
      customDataPerApp: formData.customDataPerApp,
      invoiceNumber: formData.invoiceNumber,
      invoiceDate: formData.invoiceDate?.utc(),
      paymentTerms: formData.paymentTerms ?? null,
      dueDate: formData.dueDate?.utc() ?? null,
      jobIds: undefined,
      memo: formData.memo,
    })
    emit('show-snackbar', 'Batch saved')
    batchUpdated()
  } catch (e) {
    emit('show-snackbar-error', errorMessage.value)
  }
}

function batchUpdated() {
  emit('batch-updated')
}
</script>
<style scoped>
.sync-target-form {
  margin-top: 2rem;
  margin-bottom: 2rem;
}
</style>
